import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { colors, media, layout, remCalc } from '@prisjakt/ui-base';
import HideableBox, { SPEEDS } from '@prisjakt/ui-hideable-box';
import AppsIconMDI from 'mdi-react/AppsIcon';
import CrossIconMDI from 'mdi-react/CloseIcon';
import SearchIconMDI from 'mdi-react/MagnifyIcon';
import TooltipComponent from '@prisjakt/ui-tooltip-new';
import Viewport from '../../components/viewport';

const headerBackgroundColor = colors.lightBlue1;

const logoHeight = {
  small: '30px',
  medium: '35px',
  large: '38px',
};

export const height = {
  desktop: layout.headerHeight.desktop.rem,
  mobile: layout.headerHeight.mobile.rem,
  expanded: layout.headerHeight.expanded.rem,
  mobileWithSearch: layout.headerHeight.mobileWithSearch.rem,
};

const headerMaxWidth = layout.pageContent.maxWidth;

// The viewport width above which the collapsed sidebar no longer occupies the same horizontal area as the header content
const sidebarOutsideHeaderWidth =
  headerMaxWidth.value + layout.sidebarMenuWidth.desktop.value * 2;

const mobileHeaderHeight = props => {
  const { mobileSearchOpen, expandedOnMobile } = props;

  if (!mobileSearchOpen) {
    return layout.headerHeight.mobile.value;
  }

  return expandedOnMobile
    ? layout.headerHeight.mobileExpanded.value
    : layout.headerHeight.mobileWithSearch.value;
};

const scaledMobileHeaderHeight = props =>
  mobileHeaderHeight(props) / layout.headerHeight.mobile.value;

export const FixedMargin = styled.div(props => [
  {
    display: props.theme.layout === 'raw-mobile' ? 'none' : 'block',
    height: height.desktop,
    transition: 'height 300ms ease',
    willChange: 'height',
  },

  media.smallOnly({
    height: remCalc(mobileHeaderHeight(props)),
  }),
]);

export const HeaderBar = styled.header.attrs({ 'data-test': 'Header' })`
  display: ${p => (p.theme.layout === 'raw-mobile' ? 'none' : 'block')};
  background: ${headerBackgroundColor};
  width: 100%;
  position: fixed;
  top: 0;
  height: ${height.desktop};
  /* Should be above mega menu */
  z-index: 101;

  ${media.smallOnly`
    background: transparent;
    height: auto;
    max-height: 145px;

    /* Use a pseudo-element for the background in order to be able to transition the
       transform property when the header height changes. This is much smoother than
       e.g. transitioning the actual height of the header. */
    ::before {
      background: ${headerBackgroundColor};
      content: '';
      display: block;
      height: ${layout.headerHeight.mobile.value}px;
      /* Poke out to the left to be visible in the sidebar view */
      left: -50px;
      position: absolute;
      right: 0;
      top: 0;
      transform: scaleY(${props => scaledMobileHeaderHeight(props)});
      transform-origin: 0 0;
      transition: transform 0.2s ease-out;
      will-change: transform;
      z-index: -1;
    }
  `}
`;

export const Wrapper = styled.div`
  background: ${headerBackgroundColor};
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  max-width: ${headerMaxWidth.rem};
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  /* Should be above SlidingBox */
  z-index: 2;

  ${media.smallOnly`
    height: ${height.mobile};
  `}

  @media screen and (min-width: ${
    headerMaxWidth.value
  }px) and (max-width: ${sidebarOutsideHeaderWidth}px) {
    &::before {
      content: '';
      display: block;
      width: 25%;
    }
  }
`;

const columnStyles = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100%;
`;

export const MenuItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 50px;
  height: 100%;
  ${props =>
    props.wideOnTablet &&
    media.mediumOnly`
    width: 100px;
  `}
`;

export const MenuColumn = styled.div`
  ${columnStyles};
  width: auto;
  ${media.mediumUp`
    display: none;
  `};
`;

export const MegaMenuItem = styled(TooltipComponent)`
  height: ${height.desktop};

  @media screen and (min-width: ${sidebarOutsideHeaderWidth + 1}px) {
    left: 0;
    position: fixed;
    top: 0;
  }

  ${media.mediumDown`
    display: none;
  `}
`;

export const MegaMenuButton = styled.button`
  cursor: pointer;
  display: block;
  height: 100%;
  width: ${layout.sidebarMenuWidth.desktop.rem};
  background: ${p => (p.active ? 'white' : 'transparent')};
  color: ${p => (p.active ? p.theme.icons.callToAction : p.theme.header.fg)};
  transition: background-color 0.1s linear, color 0.1s linear;

  &:hover,
  &.focus-visible {
    background: ${p => (p.active ? 'white' : p.theme.buttons.primary.bg)};
  }

  /* Double selector to override aggressive user-agent styles in e.g. Firefox */
  &:focus:focus {
    outline: none;
  }
`;

export const MegaMenuIcon = styled(AppsIconMDI).attrs(p => ({
  color: p.theme.header.fg,
}))`
  fill: currentColor;
`;

export const CloseMegaMenuIcon = styled(CrossIconMDI).attrs(p => ({
  color: p.theme.header.fg,
}))`
  fill: currentColor;
`;

export const LogoColumn = styled.div`
  ${columnStyles}
  width: 25%;

  /* Special case for a certain interval of viewport widths where we set this whole column as fixed position and aligned to the left.
     This is because we want the mega menu button to align with the sidebar, but it also has to play nicely with the logo when they "collide".
     See also the Wrapper component which adds a pseudo-element at this same interval to reserve space for the column. */
  @media screen and (min-width: ${headerMaxWidth.value}px) and (max-width: ${sidebarOutsideHeaderWidth}px) {
    position: fixed;
    left: 0;
    height: ${height.desktop};
  }

  ${media.smallOnly`
    height: ${height.mobile};
    width: ${props => (props.leftAlignedOnMobile ? '50%' : 'auto')};
  `}
`;

export const LogoLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 0 10px;

  ${media.mediumOnly`
    margin-left: 10px;
  `};

  ${media.showOutsiderAd`
    margin-left: 0;
  `};

  img {
    height: ${logoHeight.small};

    ${media.mediumUp`
      height: ${logoHeight.medium};
    `};

    ${media.largeUp`
      height: ${logoHeight.large};
    `};
  }

  &.focus-visible {
    background: ${p => p.theme.buttons.primary.bg};
    outline: none;
  }
`;

export const PromoText = styled.span`
  display: block;
  white-space: nowrap;
  font-size: ${remCalc(12)};
  color: rgba(255, 255, 255, 0.8);

  ${media.smallOnly`
    margin-top: 10px;
    font-size: ${remCalc(14)};
  `}
`;

export const SearchColumn = styled(Viewport)`
  ${columnStyles}
  position: relative;
  width: 50%;
  margin-left: 20px;
  margin-right: 20px;
  align-items: center;

  ${media.smallOnly`
    width: 100%;
    margin: 0;
    padding: 10px;
    flex-direction: column;
  `}
`;

export const RightColumn = styled.div`
  ${columnStyles}
  justify-content: flex-end;
  width: 25%;

  ${media.smallOnly`
    flex: 0 0 50px;
    width: auto;
  `}
`;

export const ClassicSwitchLink = styled.span`
  color: #fff;
  margin: 0 10px;
  text-decoration: underline;

  span {
    color: inherit;
  }
`;

export const SearchIcon = styled(SearchIconMDI).attrs(p => ({
  color: p.theme.header.fg,
}))``;

export const FadingBox = styled(HideableBox).attrs({ speed: SPEEDS.FAST })``;

export const SearchBox = styled(FadingBox)`
  width: 100%;
  /* Should be below Wrapper */
  z-index: 1;
`;
